import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { PropsWithChildren, ReactNode } from 'react';
import { useRef, useState } from 'react';
import type { PopupProps } from 'semantic-ui-react';
import { Popup } from 'semantic-ui-react';
import { useIsomorphicLayoutEffect, useResizeObserver } from 'usehooks-ts';

import s from './truncate-text.scss';

export type TruncateTextProps = PropsWithChildren<
  Omit<PopupProps, 'content' | 'trigger'>
> & {
  /**
   * Clamp across multiple lines (count from the `--truncate-text-lines` CSS
   * var, default 2) instead of truncating to a single line.
   */
  multiline?: boolean;
  renderContent?: (content: ReactNode) => ReactNode;
};

/**
 * Wraps content in a Popup containing the full text, if the content is too long
 * to fit in its container. Single-line content is truncated with an ellipsis
 * when its inline length exceeds the max-width of its parent; with `multiline`
 * it is clamped to `--truncate-text-lines` lines instead. When the content does
 * not need to be truncated, the Popup is disabled.
 */
export const TruncateText = ({
  children,
  className,
  disabled,
  multiline = false,
  position = 'top center',
  mouseEnterDelay = 250,
  renderContent = content => content,
  ...popupProps
}: TruncateTextProps) => {
  useStyles(s);

  const ref = useRef<HTMLSpanElement>(null);

  const { width, height } = useResizeObserver({ ref });

  const [isOverflowing, setIsOverflowing] = useState(false);

  // Multi-line clamps overflow vertically, single-line horizontally. width and
  // height are deps so we re-measure on resize.
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    setIsOverflowing(
      multiline
        ? element.scrollHeight > element.clientHeight
        : element.scrollWidth > element.clientWidth,
    );
  }, [children, multiline, width, height]);

  const trigger = (
    <span ref={ref} className={classNames(s.trigger, multiline && s.clamp)}>
      {children}
    </span>
  );

  return (
    <Popup
      role="tooltip"
      className={classNames(s.popup, className)}
      content={renderContent(children)}
      disabled={disabled ?? !isOverflowing}
      mouseEnterDelay={mouseEnterDelay}
      position={position}
      trigger={trigger}
      {...popupProps}
    />
  );
};

export default TruncateText;
